import { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PatientFormData } from '../../patients/types/patient.types';
import { AppointmentFormData } from '../types/appointment.types';
import appointmentBookingService from '../services/appointmentBooking.service';

interface UseAppointmentBookingProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useAppointmentBooking = ({ onSuccess, onError }: UseAppointmentBookingProps = {}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  // const navigate = useNavigate();

  const bookAppointment = async (
    patientData: PatientFormData | null, 
    appointmentData: Omit<AppointmentFormData, 'patientId'> & { patientId?: string },
    isNewPatient: boolean
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (isNewPatient && patientData) {
        // For new patients, register them first, then book appointment
        const result = await appointmentBookingService.registerPatientAndBookAppointment({
          patientData,
          appointmentData
        });
        
        enqueueSnackbar('Patient registered and appointment booked successfully!', { variant: 'success' });
        
        if (onSuccess) onSuccess();
        return result;
      } else if (!isNewPatient && appointmentData.patientId) {
        // For existing patients, just book the appointment
        const appointment = await appointmentBookingService.bookAppointmentForExistingPatient(
          appointmentData.patientId,
          appointmentData
        );
        
        enqueueSnackbar('Appointment booked successfully!', { variant: 'success' });
        
        if (onSuccess) onSuccess();
        return { appointment };
      } else {
        throw new Error('Invalid booking data');
      }
    } catch (err) {
      console.error('Error booking appointment:', err);
      const error = err as Error;
      setError(error);
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      
      if (onError) onError(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    bookAppointment,
    isSubmitting,
    error
  };
};

export default useAppointmentBooking;
